'use client';
import React from 'react';

const heritageHighlights = [
{
  icon: '🕍',
  title: 'Historic Synagogues',
  description:
  'Visit Morocco\'s magnificent synagogues, including the Beth-El Synagogue in Casablanca, the Ibn Danan Synagogue in Fes, and the Slat al-Azama in Marrakech — each a testament to centuries of Jewish worship.'
},
{
  icon: '🏘️',
  title: 'Jewish Mellahs',
  description:
  'Explore the historic Jewish quarters (mellahs) of Fes, Marrakech, Casablanca, Meknes, and Essaouira. These neighborhoods preserve the architecture, culture, and memory of Morocco\'s once-thriving Jewish communities.'
},
{
  icon: '⚪',
  title: 'Jewish Cemeteries',
  description:
  'Pay respects at Morocco\'s ancient Jewish cemeteries, some dating back over 500 years. These sacred sites hold the graves of renowned rabbis, scholars, and generations of Moroccan Jewish families.'
},
{
  icon: '✨',
  title: 'Rabbinical Shrines',
  description:
  'Visit the shrines of revered Moroccan rabbis and tzaddikim, including Rabbi Amram Ben Diwan in Ouezzane and Rabbi Haim Pinto in Mogador. These sites draw pilgrims from around the world.'
},
{
  icon: '🏙️',
  title: 'Jewish Museums',
  description:
  'Discover the Museum of Moroccan Judaism in Casablanca — the only Jewish museum in the Arab world — and other cultural institutions preserving Morocco\'s extraordinary Jewish heritage.'
},
{
  icon: '👨‍👩‍👧‍👦',
  title: 'Genealogy Research',
  description:
  'Trace your family roots in Morocco. David assists with genealogy research, connecting families to their ancestral communities, cemeteries, and historical records across the country.'
},
{
  icon: '🤝',
  title: 'Cultural Coexistence',
  description:
  'Understand Morocco\'s remarkable tradition of Jewish-Muslim coexistence. For over 2,000 years, Jewish and Muslim communities have lived side by side, creating a unique cultural tapestry.'
},
{
  icon: '🍽️',
  title: 'Kosher-Friendly Travel',
  description:
  'David assists with kosher meal planning, identifying kosher restaurants and accommodations, and ensuring your dietary requirements are met throughout your Morocco journey.'
}];


const keySites = [
'Ibn Danan Synagogue, Fes',
'Beth-El Synagogue, Casablanca',
'Slat al-Azama, Marrakech',
'Museum of Moroccan Judaism',
'Mellah of Fes',
'Mellah of Marrakech',
'Rabbi Amram Ben Diwan Shrine',
'Rabbi Haim Pinto Shrine',
'Jewish Cemetery, Fes',
'Jewish Cemetery, Marrakech'];


export default function JewishHeritage() {
  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section id="jewish-heritage" className="section-padding bg-white">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="tag-gold">✡️ Jewish Heritage</span>
          <h2 className="section-title mt-4 mb-4">
            Morocco&apos;s Extraordinary
            <br />
            <span className="text-gold italic">Jewish Heritage</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4">
            For over 2,500 years, Jewish communities have been an integral part of Morocco&apos;s cultural
            fabric. Discover this remarkable history through immersive, expertly guided private tours.
          </p>
        </div>

        {/* Hero Banner */}
        <div className="relative mb-16 animate-on-scroll">
          <div className="relative h-80 md:h-96 overflow-hidden">
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_199c4afd2-1783465024295.png"
              alt="Historic Jewish synagogue in Morocco with traditional Moorish architecture and Star of David decorations"
              className="w-full h-full object-cover"
              loading="lazy" />
            
            <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/85 via-navy-dark/50 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="container-luxury">
                <div className="max-w-xl">
                  <div className="text-4xl mb-4">✡️</div>
                  <h3 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
                    2,500 Years of
                    <br />
                    <span className="text-gold italic">Jewish Life in Morocco</span>
                  </h3>
                  <p className="font-sans text-white/85 leading-relaxed mb-6">
                    Morocco is home to one of the world&apos;s oldest and most significant Jewish diaspora
                    communities. From the ancient mellahs of Fes to the synagogues of Casablanca,
                    Morocco&apos;s Jewish heritage is a story of resilience, culture, and remarkable coexistence.
                  </p>
                  <button
                    onClick={() => handleNavClick('#contact')}
                    className="btn-gold">
                    
                    Book a Jewish Heritage Tour
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Heritage Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {heritageHighlights.map((item, index) =>
          <div
            key={item.title}
            className="heritage-card animate-on-scroll"
            style={{ transitionDelay: `${index % 4 * 100}ms` }}>
            
              <div className="text-3xl mb-4">{item.icon}</div>
              <h3 className="font-serif text-lg font-bold text-navy mb-2 group-hover:text-gold transition-colors duration-300">
                {item.title}
              </h3>
              <div className="w-6 h-0.5 bg-gold mb-3 group-hover:w-12 transition-all duration-300" />
              <p className="font-sans text-sm text-gray-600 leading-relaxed">{item.description}</p>
            </div>
          )}
        </div>

        {/* Key Destinations */}
        <div className="bg-cream p-8 md:p-12 animate-on-scroll">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="tag-gold">Key Jewish Sites</span>
              <h3 className="font-serif text-3xl font-bold text-navy mt-4 mb-4">
                Sacred Sites &amp; Historic Communities
              </h3>
              <div className="gold-divider-left" />
              <p className="font-sans text-gray-700 leading-relaxed mb-6">
                Morocco&apos;s Jewish heritage is spread across the entire country. From the ancient
                synagogues of Fes and Marrakech to the Jewish museums of Casablanca, every city
                holds treasures waiting to be discovered.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {keySites.map((site) =>
                <div key={site} className="flex items-start gap-2 font-sans text-sm text-gray-700">
                    <span className="text-gold mt-0.5 flex-shrink-0">✡️</span>
                    {site}
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_199c4afd2-1783465024295.png"
                alt="Interior of historic Moroccan synagogue with ornate decorations, Hebrew inscriptions, and Jewish artifacts"
                className="w-full h-80 object-cover shadow-luxury"
                loading="lazy" />
              
              <div className="absolute -bottom-4 -left-4 bg-gold text-navy-dark p-4 shadow-gold">
                <div className="font-serif text-2xl font-bold">2,500+</div>
                <div className="font-sans text-xs uppercase tracking-wider">Years of History</div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Quote */}
        <div className="mt-12 bg-navy p-8 md:p-12 animate-on-scroll">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-gold text-5xl font-serif leading-none mb-4">&ldquo;</div>
            <p className="font-serif text-xl md:text-2xl text-white/90 italic leading-relaxed mb-6">
              David helped us trace our family roots in the Mellah of Fes. It was an incredibly
              emotional and meaningful journey. He had done research in advance and helped us find
              our great-grandmother&apos;s grave. This was not just a tour — it was a life-changing experience.
            </p>
            <div className="font-sans text-gold font-semibold">Rachel &amp; David Levy — Tel Aviv, Israel 🇮🇱</div>
            <div className="font-sans text-white/60 text-sm mt-1">Genealogy &amp; Heritage Tour</div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-14 animate-on-scroll">
          <h3 className="font-serif text-2xl font-bold text-navy mb-4">
            Begin Your Jewish Heritage Journey
          </h3>
          <p className="font-sans text-gray-600 mb-6 max-w-xl mx-auto">
            Whether you&apos;re tracing family roots, exploring history, or seeking spiritual connection,
            David will guide you through Morocco&apos;s remarkable Jewish heritage.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => handleNavClick('#contact')} className="btn-gold">
              Book Jewish Heritage Tour
            </button>
            <a
              href="https://wa.me/212668702424?text=Hello%20David%2C%20I%20am%20interested%20in%20a%20Jewish%20Heritage%20Tour%20in%20Morocco."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold">
              
              WhatsApp David
            </a>
          </div>
        </div>
      </div>
    </section>);

}